import {createSlice, PayloadAction} from "@reduxjs/toolkit"
import {limitOptionsSelect} from "../helps/optionsSelect"
import {asyncCreator} from "./sliceHelper"
import {psLeakDeviceApi} from "../api/psLeakDeviceApi"
import {WSPSLeakDevice} from "../types/WebSocketTypes"
import {PSLeakDeviceResponse, PSLeakDevicesResponse} from "../models/response"

export type PSLeakDeviceFilter = {
    limit: number
    offset: number
    status?: "-1" | "0" | "1" | "2"
}

export type PSLeakDeviceSideDeviceLogsFilter = {
    limitDeviceLogs: number
    offsetDeviceLogs: number
    tabKey: string
    dateFromDeviceLogs?: string | null | undefined
    dateToDeviceLogs?: string | null | undefined
    searchDeviceLogs?: string
}

export type PSLeakDeviceSideDeviceLocalLogsFilter = {
    limitDeviceLocalLogs: number
    offsetDeviceLocalLogs: number
    tabKey: string
    statusDeviceLocalLogs?: '-1' | '0' | '1' | '2'
    dateFromDeviceLocalLogs?: string | null | undefined
    dateToDeviceLocalLogs?: string | null | undefined
    searchDeviceLocalLogs?: string
}

export type PSLeakDeviceSideIndicationsFilter = {
    limitIndications: number
    offsetIndications: number
    tabKey: string
    searchIndications?: string
}

export type PSLeakDeviceSideSecurityLogsFilter = {
    limitSecurityLogs: number
    offsetSecurityLogs: number
    tabKey: string
    idUser?: number
    changeMode?: "MANUAL" | "SCHEDULE" | "DEVICE"
    dateFromSecurityLogs?: string | null | undefined
    dateToSecurityLogs?: string | null | undefined
    searchSecurityLogs?: string
}

export type PSLeakDeviceFilterQuery = {
    idCompany?: number
    idObjectGroup?: number
    idObject?: number
    search?: string
}

type InitialStateType = {
    psLeakDevices: null | PSLeakDevicesResponse
    psLeakDevice: null | PSLeakDeviceResponse
    filter: PSLeakDeviceFilter & PSLeakDeviceFilterQuery
    sideFilter: PSLeakDeviceSideDeviceLogsFilter & PSLeakDeviceSideDeviceLocalLogsFilter & PSLeakDeviceSideIndicationsFilter & PSLeakDeviceSideSecurityLogsFilter
    ws: WSPSLeakDevice | null
}

const initialState: InitialStateType = {
    psLeakDevices: null,
    psLeakDevice: null,
    filter: {
        limit: limitOptionsSelect[0].value,
        offset: 0,
    },
    sideFilter: {
        limitDeviceLogs: limitOptionsSelect[0].value,
        offsetDeviceLogs: 0,
        limitDeviceLocalLogs: limitOptionsSelect[0].value,
        offsetDeviceLocalLogs: 0,
        limitIndications: limitOptionsSelect[0].value,
        offsetIndications: 0,
        limitSecurityLogs: limitOptionsSelect[0].value,
        offsetSecurityLogs: 0,
        tabKey: "1",
    },
    ws: null,
}

export const psLeakDeviceReducer = createSlice({
    name: "psLeakDeviceReducer",
    initialState,
    reducers: {
        setPSLeakDevices(state, {payload})
        {
            state.psLeakDevices = payload
        },
        setPSLeakDevice(state, {payload})
        {
            state.psLeakDevice = payload
        },
        addPSLeakDevice(state, {payload})
        {
            if (state.psLeakDevices)
            {
                state.psLeakDevices = {
                    total: state.psLeakDevices.total += 1,
                    data: [payload, ...state.psLeakDevices.data],
                }
            }
        },
        editPSLeakDevice(state, {payload}: PayloadAction<PSLeakDeviceResponse>)
        {
            let index = state.psLeakDevices?.data.findIndex(i => i.id === payload.id)
            if (index !== undefined && state.psLeakDevices) state.psLeakDevices.data[index] = payload
            if (state.psLeakDevice) state.psLeakDevice = payload
        },
        setPSLeakDeviceFilter(state, {payload})
        {
            state.filter = payload
        },
        setPSLeakDeviceSideFilter(state, {payload})
        {
            state.sideFilter = payload
        },
        setWSPSLeakDevice(state, {payload}: PayloadAction<WSPSLeakDevice | null>)
        {
            state.ws = payload
        },
    },
})

export const {setPSLeakDevices, setPSLeakDevice, addPSLeakDevice, editPSLeakDevice, setPSLeakDeviceFilter, setPSLeakDeviceSideFilter, setWSPSLeakDevice} = psLeakDeviceReducer.actions

export default psLeakDeviceReducer.reducer

export const getPSLeakDevices = asyncCreator
("psLeakDeviceReducer/getPSLeakDevices", psLeakDeviceApi.getDevices, setPSLeakDevices)

export const getPSLeakDevice = asyncCreator
("psLeakDeviceReducer/getPSLeakDevice", psLeakDeviceApi.getDevice, setPSLeakDevice)

export const createPSLeakDevice = asyncCreator
("psLeakDeviceReducer/createPSLeakDevice", psLeakDeviceApi.createDevice, addPSLeakDevice)

export const changePSLeakDevice = asyncCreator
("psLeakDeviceReducer/changePSLeakDevice", psLeakDeviceApi.changeDevice, editPSLeakDevice)

export const updateSettingsPSLeakDevice = asyncCreator
("psLeakDeviceReducer/updateSettingsPSLeakDevice", psLeakDeviceApi.updateSettingsDevice, editPSLeakDevice)

export const removeTokenPSLeakDevice = asyncCreator
("psLeakDeviceReducer/removeTokenPSLeakDevice", psLeakDeviceApi.removeToken, editPSLeakDevice)

export const deletePSLeakDevice = asyncCreator
("psLeakDeviceReducer/deletePSLeakDevice", psLeakDeviceApi.deleteDevice)

export const armingPSLeakDevice = asyncCreator
("psLeakDeviceReducer/armingPSLeakDevice", psLeakDeviceApi.armingDevice)

export const updateDeviceFirmware = asyncCreator
("psLeakDeviceReducer/updateDeviceFirmware", psLeakDeviceApi.updateDeviceFirmware)

export const rebootDevice = asyncCreator
("psLeakDeviceReducer/rebootDevice", psLeakDeviceApi.rebootDevice)

export const solveMemoryErrorPSLeakDevice = asyncCreator
("psLeakDeviceReducer/solveMemoryErrorPSLeakDevice", psLeakDeviceApi.solveMemoryError)